import { useCallback, useState } from "react";

import { useToasts } from "react-toast-notifications";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { TextInput } from "@shared/text-input";

import { useSalesSavedViews } from "../sales-saved-views-provider";
import { useSalesPageV3NextContext } from "../sales-v3-next-provider";

interface Props {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const CreateSavedViewsModal = ({
  isOpen,
  onConfirm,
  onCancel,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { addToast } = useToasts();
  const { createSavedView } = useSalesSavedViews();
  const { selectSavedView, pageConfiguration, chartState, tableState } =
    useSalesPageV3NextContext();

  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const saveSavedView = async () => {
    prettyLogEventPage({
      domain: "Top Level Filters",
      action: "onChange",
      additionalActionInfo: "Create Saved View",
    });

    try {
      if (name) {
        setIsSaving(true);

        const { data } = await createSavedView(name, {
          pageConfiguration,
          chartState,
          tableState,
        });

        if (data?.createSalesSavedView) {
          selectSavedView(data.createSalesSavedView);
        }

        onConfirm();
        addToast("Saved view created", { appearance: "success" });
      }
    } finally {
      setIsSaving(false);
    }

    setName("");
  };

  const cancel = useCallback(() => {
    onCancel();
    setName("");
  }, [onCancel]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={cancel}
      headerTitleProps={{ className: "text-xl font-semibold" }}
      titleContent={<div>Save as new?</div>}
    >
      <TextInput
        label="SAVED VIEW NAME"
        onChange={({ target }) => setName(target.value)}
        value={name}
      />
      <div className="flex space-x-3 mt-12">
        <PrimaryButton
          isDisabled={!name || isSaving}
          loading={isSaving}
          onClick={saveSavedView}
        >
          YES, SAVE
        </PrimaryButton>
        <SecondaryButton disabled={isSaving} onClick={cancel}>
          NO, CANCEL
        </SecondaryButton>
      </div>
    </Modal>
  );
};
